import { useEffect, useRef, useState } from "react";
import { CrmLayout } from "@/components/crm/CrmLayout";
import { api, apiError, resolveAsset } from "@/lib/api";
import { CONTACT_STATUS } from "@/lib/crmConstants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Globe, Loader2, Upload, Plug } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const empty = {
  commercial_name: "", legal_entity: "", official_website: "", logo_url: "",
  product_category: "mutuelle_france", professional_contact: "", contact_status: "a_identifier",
  distribution_agreement_verified: false, api_available: false, technical_documentation: "",
  integration_status: "a_identifier", api_connected: false, api_source: "", api_base_url: "",
  api_credentials_ref: "", internal_notes: "", active: true,
};

const API_SOURCES = [
  { key: "", label: "—" },
  { key: "direct", label: "Direct (assureur)" },
  { key: "oggo", label: "Oggo Data" },
  { key: "utwin", label: "Utwin" },
  { key: "assurly", label: "Assurly" },
  { key: "autre", label: "Autre agrégateur" },
];

function LogoThumb({ name, url }) {
  const src = resolveAsset(url);
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    const letter = (name || "?").trim().charAt(0).toUpperCase();
    return <div className="w-11 h-11 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center font-extrabold text-sm shrink-0">{letter}</div>;
  }
  return <img src={src} alt={name} onError={() => setFailed(true)} className="w-11 h-11 rounded-xl object-contain bg-white border border-slate-100 shrink-0" />;
}

export default function Insurers() {
  const { user } = useAuth();
  const readOnly = user.role === "read_only";
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("mutuelle_france");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/crm/insurers").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (i) => { setForm({ ...empty, ...i }); setEditing(i.id); setOpen(true); };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/crm/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: r.data.logo_url }));
      toast.success("Logo téléversé.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    try {
      if (editing) await api.patch(`/crm/insurers/${editing}`, form);
      else await api.post("/crm/insurers", form);
      toast.success("Enregistré."); setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (loading) return <CrmLayout><div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-blue-600" /></div></CrmLayout>;
  const filtered = items.filter((i) => i.product_category === tab);

  return (
    <CrmLayout>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-display text-2xl font-bold text-slate-900">Référentiel des assureurs</h1>
          <p className="text-slate-500 text-sm mt-1">Partenaires potentiels à vérifier. Une offre n'est publiée comme disponible qu'après validation de la distribution.</p></div>
        {!readOnly && <Button onClick={openNew} data-testid="add-insurer"><Plus size={16} className="mr-1" />Ajouter</Button>}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-5">
        <TabsList><TabsTrigger value="mutuelle_france">Mutuelle France</TabsTrigger><TabsTrigger value="expatrie">Expatrié</TabsTrigger></TabsList>
      </Tabs>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
        {filtered.map((i) => (
          <Card key={i.id} className="p-5" data-testid={`insurer-card-${i.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <LogoThumb name={i.commercial_name} url={i.logo_url} />
                <div className="min-w-0"><h3 className="font-semibold text-slate-900 truncate">{i.commercial_name}</h3>{i.legal_entity && <p className="text-xs text-slate-500 truncate">{i.legal_entity}</p>}</div>
              </div>
              {!readOnly && <Button size="icon" variant="ghost" onClick={() => openEdit(i)} data-testid={`edit-insurer-${i.id}`}><Pencil size={15} /></Button>}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              <Badge variant="secondary" className="text-xs">{CONTACT_STATUS.find((c) => c.key === i.contact_status)?.label || i.contact_status}</Badge>
              {i.distribution_agreement_verified && <Badge className="bg-emerald-600 text-xs">Accord vérifié</Badge>}
              {i.api_connected ? <Badge className="bg-blue-600 text-xs">API connectée</Badge> : i.api_available && <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">API dispo.</Badge>}
            </div>
            {i.official_website && <a href={i.official_website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-blue-700 mt-3 hover:underline"><Globe size={12} />{i.official_website}</a>}
            {i.last_verified_at && <p className="text-xs text-slate-400 mt-2">Vérifié le {new Date(i.last_verified_at).toLocaleDateString("fr-FR")}</p>}
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifier" : "Ajouter"} un assureur</DialogTitle>
            <DialogDescription>Renseignez les informations du partenaire, son logo et le branchement API.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="mb-1.5 block">Nom commercial</Label><Input value={form.commercial_name} onChange={(e) => setForm({ ...form, commercial_name: e.target.value })} data-testid="insurer-name" /></div>
            <div><Label className="mb-1.5 block">Entité juridique</Label><Input value={form.legal_entity} onChange={(e) => setForm({ ...form, legal_entity: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Site officiel</Label><Input value={form.official_website} onChange={(e) => setForm({ ...form, official_website: e.target.value })} placeholder="https://" /></div>

            {/* Logo : upload fichier OU URL */}
            <div>
              <Label className="mb-1.5 block">Logo</Label>
              <div className="flex items-center gap-3">
                <LogoThumb name={form.commercial_name} url={form.logo_url} />
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif" className="hidden" onChange={onPickFile} data-testid="insurer-logo-file" />
                <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="insurer-logo-upload-btn">
                  {uploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}Téléverser
                </Button>
                {form.logo_url && <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, logo_url: "" })}>Retirer</Button>}
              </div>
              <Input className="mt-2" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="… ou collez une URL de logo" data-testid="insurer-logo" />
              <p className="text-xs text-slate-400 mt-1">PNG, JPG, WEBP, SVG — max 3 Mo.</p>
            </div>

            <div><Label className="mb-1.5 block">Catégorie</Label>
              <Select value={form.product_category} onValueChange={(v) => setForm({ ...form, product_category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="mutuelle_france">Mutuelle France</SelectItem><SelectItem value="expatrie">Expatrié</SelectItem></SelectContent></Select></div>
            <div><Label className="mb-1.5 block">Contact professionnel</Label><Input value={form.professional_contact} onChange={(e) => setForm({ ...form, professional_contact: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-xs">Statut de contact</Label>
                <Select value={form.contact_status} onValueChange={(v) => setForm({ ...form, contact_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CONTACT_STATUS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="mb-1.5 block text-xs">Statut d'intégration</Label>
                <Select value={form.integration_status} onValueChange={(v) => setForm({ ...form, integration_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CONTACT_STATUS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.distribution_agreement_verified} onCheckedChange={(c) => setForm({ ...form, distribution_agreement_verified: !!c })} data-testid="insurer-agreement" />Accord de distribution vérifié</label>

            {/* Branchement API — structure générique */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Plug size={15} className="text-blue-600" />Branchement API (tarification)</div>
              <p className="text-xs text-slate-500 -mt-1">Configuration à préparer dès maintenant. L'appel réel sera activé lorsque vous disposerez du contrat et des clés (direct ou agrégateur).</p>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.api_available} onCheckedChange={(c) => setForm({ ...form, api_available: !!c })} />API disponible chez l'assureur</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.api_connected} onCheckedChange={(c) => setForm({ ...form, api_connected: !!c })} data-testid="insurer-api-connected" />API connectée (active)</label>
              <div><Label className="mb-1.5 block text-xs">Source / agrégateur</Label>
                <Select value={form.api_source || ""} onValueChange={(v) => setForm({ ...form, api_source: v })}>
                  <SelectTrigger data-testid="insurer-api-source"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{API_SOURCES.filter((s) => s.key).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="mb-1.5 block text-xs">URL de base de l'API</Label><Input value={form.api_base_url} onChange={(e) => setForm({ ...form, api_base_url: e.target.value })} placeholder="https://api.partenaire.com/v1" data-testid="insurer-api-base-url" /></div>
              <div><Label className="mb-1.5 block text-xs">Référence des identifiants (nom de variable)</Label><Input value={form.api_credentials_ref} onChange={(e) => setForm({ ...form, api_credentials_ref: e.target.value })} placeholder="ex. OGGO_API_KEY" data-testid="insurer-api-credentials-ref" /><p className="text-xs text-slate-400 mt-1">N'inscrivez jamais la clé en clair ici — uniquement le nom de la variable d'environnement.</p></div>
              <div><Label className="mb-1.5 block text-xs">Documentation technique</Label><Input value={form.technical_documentation} onChange={(e) => setForm({ ...form, technical_documentation: e.target.value })} placeholder="Lien vers la doc API" /></div>
            </div>

            <div><Label className="mb-1.5 block">Notes internes</Label><Input value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-insurer">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </CrmLayout>
  );
}
